import {default as cx} from "classnames"
import {Skeleton} from "primereact/skeleton"
import {useState} from "react"
import {useNavigate, useParams, useSearchParams} from "react-router-dom"
import useSWR from "swr"

import {AssessedScoresRow} from "../components/educator/AssessedScoresRow"
import {CumulativeDetails} from "../components/educator/CumulativeDetails"
import {EducatorProgressReport} from "../components/educator/EducatorProgressReport"
import {ProgressCardHeader} from "../components/educator/ProgressCardHeader"
import {SupportScoresRow} from "../components/educator/SupportScoresRow"
import {WeekDetailsAggregateAdditionalTab} from "../components/educator/WeekDetailsAggregateAdditionalTab"
import {WeekDetailsNHSRs} from "../components/educator/WeekDetailsNHSRs"
import {getNhsrSubmissionStatus, isReportMissing} from "../utilities/reportStatus"
import {fetcherGet, getLocaleDate, scoreBadge} from "../utilities/general"

const WeekDetailsAggregateRFPTab = ({week, badge, percent, osrs}) => {
    return (
        <div className="tab-pane show active" id="rfp-tab" role="tabpanel">
            <div className="d-flex align-items-center w-100 flex-column">
                <div className="d-flex justify-content-between w-100 mb-3">
                    <span className="fw-bold fs-4">
                        Aggregate Ready for Practice Score:
                        <div className={`badge badge-lg ${badge.background} ${badge.textColor} ms-2 me-5`}>
                            {badge.badge}
                        </div>
                    </span>
                    <span className="fw-bold fs-4">{percent}%</span>
                </div>
                <div className="h-8px mx-3 rounded w-100 bg-secondary mb-5">
                    <div className={`rounded h-8px ${badge.color}`} style={{width: `${badge.width}%`}}></div>
                </div>
                <div className="d-flex justify-content-between w-100 my-3">
                    <span className="fw-bold fs-4">Individual Week {week} Readiness Scores</span>
                </div>
                <div className="d-flex align-items-center  w-100 flex-column gap-3">
                    {osrs &&
                        osrs.map((osr) => {
                            const badge = scoreBadge(osr.osr_score.val, osr.osr_score.max)

                            return (
                                <div className="d-flex justify-content-between w-100" key={osr.response_id}>
                                    <div>
                                        <span className="fw-bold fs-5 me-4">{osr.preceptor_name}</span>
                                        <span className=" fs-5">Shift Date {getLocaleDate(osr.shift_date)}</span>
                                    </div>
                                    <span className="d-flex align-items-center fw-bold fs-5">
                                        <div
                                            className={`badge badge-lg ${badge.background} ${badge.textColor} ms-2 me-5`}
                                        >
                                            {badge.badge}
                                        </div>
                                        {osr.osr_score.percent}%
                                    </span>
                                </div>
                            )
                        })}
                </div>
            </div>
        </div>
    )
}

const WeekDetailsAggregateCategoryTab = ({id, name, badge, percent, osrs}) => {
    const qas = {}
    osrs.forEach((osr) => {
        osr.answers
            .filter((que) => que.category === name && que.question_type_id !== 10)
            .forEach((answer) => {
                if (!qas[answer.question_order]) {
                    qas[answer.question_order] = {
                        question: answer.question_text,
                        sum: 0,
                        max: answer.max_answer_value,
                        answers: [],
                    }
                }
                qas[answer.question_order].sum += parseInt(answer.answer)
                const qa = {shiftDate: osr.shift_date, preceptorName: osr.preceptor_name, answer: answer.answer}
                qas[answer.question_order].answers.push(qa)
            })
    })

    return (
        <div className="tab-pane show active" id={`${id}-tab`} role="tabpanel">
            <div className="d-flex align-items-center w-100 flex-column">
                <div className="d-flex justify-content-between w-100 mb-3">
                    <span className="fw-bold fs-4">Aggregate {name} Score:</span>
                    <span className="fw-bold fs-4">{percent}%</span>
                </div>
                <div className="h-8px mx-3 rounded w-100 bg-secondary mb-5">
                    <div className={`rounded h-8px ${badge.color}`} style={{width: `${badge.width}%`}}></div>
                </div>
                {Object.keys(qas).map((key) => {
                    const question = qas[key]
                    const aggregateScore = Math.round((question.sum / question.answers.length) * 100) / 100

                    return (
                        <div key={key} className="w-100">
                            <div className="w-100">
                                <div className="d-flex flex-row w-100 fs-5 gap-4 mb-2">
                                    <div className="flex-grow-1 fw-bold">{question.question}</div>
                                    <div className="d-flex  align-items-center w-100px">
                                        <div className="h-10px mx-3 rounded w-100 bg-secondary">
                                            <div
                                                className={`${badge.color} rounded h-10px`}
                                                role="progressbar"
                                                style={{
                                                    width: `${(aggregateScore / question.max) * 100}%`,
                                                }}
                                            ></div>
                                        </div>
                                        <div className="ms-1 fw-bold fs-4">
                                            {aggregateScore === 0 ? "N/A" : aggregateScore}
                                        </div>
                                    </div>
                                </div>
                                {question.answers.map((answer, index) => {
                                    return (
                                        <div
                                            className="d-flex flex-row w-100 fs-5 gap-4 mb-2"
                                            key={index + "-" + answer.question_order}
                                        >
                                            <div
                                                className="d-flex justify-content-between w-100 "
                                                key={answer.question_order}
                                            >
                                                <div>
                                                    <span className="fw-bold fs-5 me-4">
                                                        {answer.preceptorName ? answer.preceptorName : "Unknown"}
                                                    </span>
                                                    <span className=" fs-5">
                                                        Shift Date {getLocaleDate(answer.shiftDate)}
                                                    </span>
                                                </div>
                                                <span className="d-flex align-items-center fw-bold fs-5">
                                                    {answer.answer === "0" ? "N/A" : answer.answer}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div className="separator border-1 border-secondary my-5"></div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const WeekDetailsAggregateTabItem = ({tab, activeTab, setActiveTab, name, badge}) => {
    return (
        <li className="nav-item w-md-300px" role="presentation">
            <button
                className={cx("nav-link fw-semibold d-flex flex-stack w-100 text-start", {
                    active: activeTab === tab,
                })}
                onClick={() => setActiveTab(tab)}
                role="tab"
            >
                {name}
                <span className={`badge flex-center ms-2 ${badge.background} ${badge.textColor}`}>
                    {Math.round(badge.width)}%
                </span>
            </button>
        </li>
    )
}

const WeekDetails = ({week, orientation, osrs, nhsrs}) => {
    const [activeTab, setActiveTab] = useState("rfp")
    const weekNHSRs = !nhsrs
        ? []
        : nhsrs.filter((nhsr) => {
              return nhsr.week === week
          })
    const weekOSRs = !osrs
        ? []
        : osrs.filter((osr) => {
              return osr.week === week
          })
    const latestReport =
        orientation.progress_reports && orientation.progress_reports.length
            ? orientation.progress_reports
                  .filter((report) => report.week === week && report.rating_id)
                  .reduce((latest, current) => {
                      if (!latest) return current
                      return new Date(current.created_at) > new Date(latest.created_at) ? current : latest
                  }, null)
            : null
    const weekStatus = orientation?.weeks?.[week]

    let aggregateCategories = {}
    let aggregateScores = {val: 0, max: 0, percent: 0}
    if (weekOSRs && weekOSRs.length > 0) {
        weekOSRs.forEach((osr) => {
            Object.keys(osr.categories).forEach((cat) => {
                if (!aggregateCategories[cat]) {
                    aggregateCategories[cat] = {val: 0, max: 0, percent: 0}
                }
                aggregateCategories[cat].val += osr.categories[cat].val
                aggregateCategories[cat].max += osr.categories[cat].max
                aggregateCategories[cat].percent = Math.round(
                    (aggregateCategories[cat].val / aggregateCategories[cat].max) * 100
                )
            })
            aggregateScores.val += osr.osr_score.val
            aggregateScores.max += osr.osr_score.max
            aggregateScores.percent = Math.round((aggregateScores.val / aggregateScores.max) * 100)
        })
    }

    const EMPTY_BADGE = {
        badge: "",
        background: "bg-white",
        border: "border-gray-400",
        color: "",
        score: "",
        textColor: "",
        width: "",
        order: 99,
    }

    const getCategoryBadge = (categoryName) => {
        const category = aggregateCategories[categoryName]

        return category?.val ? scoreBadge(category.val, category.max) : EMPTY_BADGE
    }

    // Empty badges happen when there isn't any actual OSR data
    const osrBadge = scoreBadge(aggregateScores.val, aggregateScores.max)
    const cpBadge = getCategoryBadge("Clinical Practice")
    const acBadge = getCategoryBadge("Assessment & Care")
    const mmBadge = getCategoryBadge("Medication Management")
    const psqBadge = getCategoryBadge("Prevention, Safety & Quality")
    const edBadge = getCategoryBadge("Education, Communication & Collaboration")
    const proBadge = getCategoryBadge("Professionalism")

    const clinicalPracticeCatgory = aggregateCategories?.["Clinical Practice"]
    const asssesmentCareCategory = aggregateCategories?.["Assessment & Care"]
    const medicationMgmtCategory = aggregateCategories?.["Medication Management"]
    const professionalismCategory = aggregateCategories?.["Professionalism"]
    const preventionSafetyQualityCategory = aggregateCategories?.["Prevention, Safety & Quality"]
    const educationCategory = aggregateCategories?.["Education, Communication & Collaboration"]

    return (
        <>
            <div className="card ms-4 my-4 border border-gray-400">
                <ProgressCardHeader
                    badge={osrBadge}
                    name={`Week ${week}`}
                    osr={weekOSRs[0]}
                    osrSubmissionStatus={weekStatus?.osr_status}
                    nhsrSubmissionStatus={weekStatus?.nhsr_status}
                    progressReport={latestReport}
                    progressReportWarning={false}
                />
                <div className="card-body px-5">
                    <h3 className="w-100 mb-5 fs-4">Ready for Practice Scores</h3>
                    <AssessedScoresRow osr={weekOSRs[0]} nhsr={weekNHSRs[0]} />
                    <h3 className="w-100 mb-5 fs-4">Support Scores</h3>
                    <SupportScoresRow nhsr={weekNHSRs[0]} />
                </div>
            </div>

            <div className="card card-px-0 mb-5 ms-4 border border-gray-400">
                <div className="card-header p-4 mb-4">
                    <h3 className="card-title fw-bold">Week {week} OSR Aggregate Scores</h3>
                </div>
                <div className="card-body px-5">
                    <div className="d-flex flex-column flex-md-row">
                        <ul
                            className="nav nav-pills flex-row flex-md-column me-5 mb-3 mb-md-0 fs-6 gap-4"
                            role="tablist"
                        >
                            <li className="nav-item w-md-300px" role="presentation">
                                <button
                                    className={cx("nav-link fw-semibold d-flex flex-stack w-100 text-start", {
                                        active: activeTab === "rfp",
                                    })}
                                    onClick={() => setActiveTab("rfp")}
                                    aria-selected={activeTab === "rfp"}
                                    role="tab"
                                >
                                    Readiness for Practice
                                    <span
                                        className={`badge flex-center ms-2 ${osrBadge.background} ${osrBadge.textColor}`}
                                    >
                                        {aggregateScores.percent}%
                                    </span>
                                </button>
                            </li>
                            <WeekDetailsAggregateTabItem
                                tab="cp"
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                name="Clinical Practice"
                                badge={cpBadge}
                            />
                            <WeekDetailsAggregateTabItem
                                tab="ac"
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                name="Assessment & Care"
                                badge={acBadge}
                            />
                            <WeekDetailsAggregateTabItem
                                tab="mm"
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                name="Medication Management"
                                badge={mmBadge}
                            />
                            <WeekDetailsAggregateTabItem
                                tab="psq"
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                name="Prevention, Safety & Quality"
                                badge={psqBadge}
                            />
                            <WeekDetailsAggregateTabItem
                                tab="ed"
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                name="Education, Communication & Collaboration"
                                badge={edBadge}
                            />
                            <WeekDetailsAggregateTabItem
                                tab="pro"
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                name="Professionalism"
                                badge={proBadge}
                            />
                            <li className="nav-item w-md-300px" role="presentation">
                                <button
                                    className={cx("nav-link fw-semibold d-flex flex-stack w-100 text-start", {
                                        active: activeTab === "add",
                                    })}
                                    onClick={() => setActiveTab("add")}
                                    aria-selected={activeTab === "add"}
                                    role="tab"
                                >
                                    Addtional Input
                                </button>
                            </li>
                        </ul>
                        <div className="tab-content w-100" id="aggregateTabContent">
                            {activeTab === "rfp" && aggregateScores?.percent != null && (
                                <WeekDetailsAggregateRFPTab
                                    week={week}
                                    osrs={weekOSRs}
                                    badge={osrBadge}
                                    percent={aggregateScores.percent}
                                />
                            )}
                            {activeTab === "cp" && clinicalPracticeCatgory?.percent != null && (
                                <WeekDetailsAggregateCategoryTab
                                    id="cp"
                                    name="Clinical Practice"
                                    badge={cpBadge}
                                    percent={clinicalPracticeCatgory.percent}
                                    osrs={weekOSRs}
                                />
                            )}
                            {activeTab === "ac" && asssesmentCareCategory?.percent != null && (
                                <WeekDetailsAggregateCategoryTab
                                    id="ac"
                                    name="Assessment & Care"
                                    badge={acBadge}
                                    percent={asssesmentCareCategory.percent}
                                    osrs={weekOSRs}
                                />
                            )}
                            {activeTab === "mm" && medicationMgmtCategory?.percent != null && (
                                <WeekDetailsAggregateCategoryTab
                                    id="mm"
                                    name="Medication Management"
                                    badge={mmBadge}
                                    percent={medicationMgmtCategory.percent}
                                    osrs={weekOSRs}
                                />
                            )}
                            {activeTab === "psq" && preventionSafetyQualityCategory?.percent != null && (
                                <WeekDetailsAggregateCategoryTab
                                    id="psq"
                                    name="Prevention, Safety & Quality"
                                    badge={psqBadge}
                                    percent={preventionSafetyQualityCategory.percent}
                                    osrs={weekOSRs}
                                />
                            )}
                            {activeTab === "ed" && educationCategory?.percent != null && (
                                <WeekDetailsAggregateCategoryTab
                                    id="ed"
                                    name="Education, Communication & Collaboration"
                                    badge={edBadge}
                                    percent={educationCategory.percent}
                                    osrs={weekOSRs}
                                />
                            )}

                            {activeTab === "pro" && professionalismCategory?.percent != null && (
                                <WeekDetailsAggregateCategoryTab
                                    id="pro"
                                    name="Professionalism"
                                    badge={proBadge}
                                    percent={professionalismCategory.percent}
                                    osrs={weekOSRs}
                                />
                            )}
                            {activeTab === "add" && <WeekDetailsAggregateAdditionalTab osrs={weekOSRs} />}
                        </div>
                    </div>
                </div>
            </div>
            <WeekDetailsNHSRs week={week} nhsrs={weekNHSRs} />
        </>
    )
}

const EducatorOrientationDetails = () => {
    const navigate = useNavigate()
    const {orientation_id} = useParams()
    const {data: orientation, isLoading} = useSWR(`/orientation-details?orientation-id=${orientation_id}`, fetcherGet, {
        fallbackData: [],
    })
    const {data: osrs, isLoading: isOSRsLoading} = useSWR(`/orientations/${orientation_id}/osrs`, fetcherGet, {
        fallbackData: [],
    })
    const {data: nhsrs, isLoading: isNHSRsLoading} = useSWR(`/orientations/${orientation_id}/nhsrs`, fetcherGet, {
        fallbackData: [],
    })

    const week = Number.isFinite(orientation.current_week_of_orientation)
        ? orientation.current_week_of_orientation
        : Number(orientation.current_week_of_hire)
    const weeks = Number.isFinite(week) && week > 0 ? Array.from({length: week}, (_, i) => week - i) : []
    const currentWeek = orientation.current_week_of_orientation
    const currentStatus = orientation.weeks?.[currentWeek]

    const osrSubmissionStatus = currentStatus?.osr_status
    const nhsrSubmissionStatus = currentStatus?.nhsr_status

    const [searchParams] = useSearchParams()
    // Using week 0 for cumulative
    const [showWeek, setShowWeek] = useState(searchParams.get("week") ? parseInt(searchParams.get("week")) : 0)

    // Sync the week into the URL via the History API so switching is instant and
    // doesn't trigger a router navigation (which would re-run loaders).
    const selectWeek = (week) => {
        setShowWeek(week)
        const url = new URL(window.location.href)
        if (week === 0) {
            url.searchParams.delete("week")
        } else {
            url.searchParams.set("week", String(week))
        }
        window.history.replaceState(window.history.state, "", url)
    }

    if (isLoading) {
        return <Skeleton className="w-100 h-400px" />
    }

    const getMostRecentByShiftDate = (items) => {
        if (!items || items.length === 0) return null

        return items.reduce((mostRecent, current) => {
            const currentDate = new Date(current.shift_date)
            const mostRecentDate = new Date(mostRecent.shift_date)
            return currentDate > mostRecentDate ? current : mostRecent
        })
    }

    return (
        <div className="d-flex flex-column flex-grow-1">
            <div className="flex-shrink-0 bg-gray-700 px-8 py-2">
                <div className="d-flex flex-row flex-row-fluid align-items-center py-0">
                    <div className="d-flex flex-grow-1">
                        <div className="d-flex flex-column">
                            <h1 className="mb-0 text-white">{orientation.name}</h1>
                        </div>
                    </div>
                    <button
                        className="btn btn-text-white btn-active-light-primary btn-flex"
                        onClick={() => navigate("/educator-orientations")}
                    >
                        <i className="fas fa-arrow-left fs-4"></i>
                        Active Orientees
                    </button>
                    <button
                        className="btn btn-dark m-2"
                        onClick={() => {
                            navigate(`/educator-orientations/${orientation.id}/preceptors`)
                        }}
                    >
                        <span className="menu-title fw-bold px-3">Edit Preceptors</span>
                    </button>
                    <button
                        className="btn btn-dark"
                        onClick={() => {
                            navigate(`/educator-orientations/${orientation.id}/status`)
                        }}
                    >
                        <span className="menu-title fw-bold px-3">Orientee Status</span>
                    </button>
                </div>
                <div className="hover-scroll-x">
                    <div className="d-grid">
                        <ul
                            className="nav nav-tabs flex-nowrap text-nowrap bg-light-primary rounded-top"
                            role="tablist"
                        >
                            <li className="nav-item " role="presentation">
                                <button
                                    className={cx(
                                        "nav-link btn btn-flex btn-active-primary btn-active-color-light btn-light-primary rounded-bottom-0 border border-bottom-1 border-right-0 border-left-0 border-top-0 fw-bold",
                                        {active: showWeek === 0}
                                    )}
                                    onClick={() => selectWeek(0)}
                                >
                                    Progress Snapshot
                                </button>
                            </li>
                            {weeks.length > 0 &&
                                weeks.map((week) => (
                                    <li className="nav-item" key={week}>
                                        <button
                                            className={cx(
                                                "nav-link btn btn-flex btn-active-primary btn-active-color-light btn-light-primary rounded-bottom-0 border border-bottom-1 border-right-0 border-left-0 border-top-0 fw-bold",
                                                {active: showWeek === week}
                                            )}
                                            onClick={() => selectWeek(week)}
                                        >
                                            {(getNhsrSubmissionStatus(week, nhsrs) === "missing" ||
                                                isReportMissing(week, osrs) ||
                                                isReportMissing(week, orientation.progress_reports)) && (
                                                <i className="fas fa-circle text-danger"></i>
                                            )}
                                            Week {week}
                                        </button>
                                    </li>
                                ))}
                        </ul>
                    </div>
                </div>
            </div>
            <div className="d-flex row w-100">
                <div className="app-container container d-flex flex-column ps-7">
                    {isOSRsLoading || isNHSRsLoading ? (
                        <Skeleton className="w-100 h-400px" />
                    ) : showWeek === 0 ? (
                        <CumulativeDetails
                            orientation={orientation}
                            osr={getMostRecentByShiftDate(osrs) || {}}
                            nhsr={getMostRecentByShiftDate(nhsrs) || {}}
                            osrSubmissionStatus={!osrSubmissionStatus}
                            nhsrSubmissionStatus={nhsrSubmissionStatus}
                        />
                    ) : (
                        <div className="row">
                            <div className="col-4 bg-white px-10 py-6 border-end border-gray-400">
                                <EducatorProgressReport week={showWeek} orientation={orientation} osrs={osrs} />
                            </div>
                            <div className="col-8">
                                <WeekDetails orientation={orientation} week={showWeek} osrs={osrs} nhsrs={nhsrs} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default EducatorOrientationDetails
